import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import PaymentStatusStore from '../../../stores/PaymentStatusStore';
import { SubsectionHeading } from 'web-ui-components/lib/typography/headings';
import { DataTable } from 'web-ui-components/lib/molecules/tables';

import { renderAccountDetails } from '../ViewPaymentHistory';
import StatusBadgeSME from './StatusBadgeSME';
import { paymentTypes } from '../../../constants/StringConstants';
import { sortByString, sortByAmount } from '../PaymentHistoryHelper';

export const getFromToColumn = (historyDetails, content) => {
  const paymentType = get(
    historyDetails,
    'type',
    get(historyDetails, 'paymentType')
  );

  if (paymentType === paymentTypes.TYPE_BACS_DEBIT) {
    return {
      title: [content.fromLabel],
      id: 'fromAccountName',
      path: ['fromAccount'],
      sortDirection: 'asc',
    };
  }

  return {
    title: [content.toLabel],
    id: 'toAccountName',
    path: ['toAccount'],
    sortDirection: 'asc',
  };
};

export const getColumns = (historyDetails, content) => {
  if (!historyDetails.children) return [];
  const hasReference = historyDetails.children.some(
    (childPayment) => childPayment.reference
  );
  const hasTransactionCode = historyDetails.children.some(
    (childPayment) => childPayment.transactionCode
  );
  const hasHMRCReference = historyDetails.children.some(
    (childPayment) => childPayment.hmrcReference
  );
  const hasStatus = historyDetails.children.some(
    (childPayment) => childPayment.status
  );

  const hasPaymentFees = historyDetails.children.some(
    (childPayment) => childPayment.paymentFees
  );

  const columns = [
    getFromToColumn(historyDetails, content),
    {
      title: [content.amount],
      id: 'amountRaw',
      path: ['amount'],
      sortDirection: 'asc',
    },
    ...(hasReference
      ? [
          {
            title: [content.referencePaymentLabel],
            id: 'reference',
            path: ['reference'],
            sortDirection: 'asc',
          },
        ]
      : []),
    ...(hasTransactionCode
      ? [
          {
            title: [content.transactionCodeLabel],
            id: 'transactionCode',
            path: ['transactionCode'],
            sortDirection: 'asc',
          },
        ]
      : []),
    ...(hasHMRCReference
      ? [
          {
            title: [content.hmrcReferenceLabel],
            id: 'hmrcReference',
            path: ['hmrcReference'],
            sortDirection: 'asc',
          },
        ]
      : []),
    ...(hasPaymentFees
      ? [
          {
            title: [content.paymentFeesLabel],
            id: 'paymentFees',
            path: ['paymentFees'],
          },
        ]
      : []),
    ...(hasStatus
      ? [
          {
            title: [content.status],
            id: 'statusBadge',
            path: ['statusBadge'],
          },
        ]
      : []),
  ];

  return columns;
};

export const getFormattedData = (historyDetails) =>
  historyDetails.children.map((paymentDetail) => {
    const {
      status,
      fromAccountName,
      fromAccountAccountNumber,
      fromAccountSortCode,
      toAccountAccountNumber,
      toAccountSortCode,
      toAccountName,
    } = paymentDetail;

    return {
      ...paymentDetail,
      statusBadge: <StatusBadgeSME status={status} />,
      statusMsg: PaymentStatusStore.getMessageByStatusCode(
        get(paymentDetail, 'statusCode')
      ),
      fromAccount: renderAccountDetails(
        fromAccountAccountNumber,
        fromAccountSortCode,
        fromAccountName
      ),
      toAccount: renderAccountDetails(
        toAccountAccountNumber,
        toAccountSortCode,
        toAccountName
      ),
    };
  });

const sortByCompareFunc = {
  reference: sortByString('reference'),
  amountRaw: sortByAmount('amountRaw'),
  from: sortByString('fromSort'),
  fromAccountName: sortByString('fromAccountName'),
  toAccountName: sortByString('toAccountName'),
  transactionCode: sortByString('transactionCode'),
  hmrcReference: sortByString('hmrcReference'),
};

export const sortData = (data, sortByColumn) => {
  const { id, sortDirection } = sortByColumn;
  const compareFunc = sortByCompareFunc[id](sortDirection);

  return data.sort(compareFunc);
};

class ViewChildPaymentHistory extends React.Component {
  state = {
    columns: getColumns(this.props.historyDetails, this.props.content),
    sortedColumnIdx: 0,
  };

  handleSortOrder = (selectedColumn) => {
    let { columns } = this.state;

    if (!selectedColumn) return;
    const sortedColumnIdx = columns.findIndex(
      (column) => column.id === selectedColumn.id
    );
    columns[sortedColumnIdx].sortDirection =
      columns[sortedColumnIdx].sortDirection === 'asc' ? 'desc' : 'asc';

    this.setState({ columns, sortedColumnIdx });
  };

  render() {
    const { historyDetails } = this.props;
    const { columns, sortedColumnIdx } = this.state;

    const data = getFormattedData(historyDetails);
    const sortedData = sortData(data, columns[sortedColumnIdx]);

    return (
      <div className="web-ui-components">
        <SubsectionHeading>Payments</SubsectionHeading>

        <DataTable
          layout="transactions"
          columns={columns}
          data={sortedData}
          isBranded
          keyPath={['id']}
          onHeaderCellClick={this.handleSortOrder}
        />
      </div>
    );
  }
}

ViewChildPaymentHistory.propTypes = {
  content: PropTypes.object,
  historyDetails: PropTypes.shape({
    type: PropTypes.string,
    paymentType: PropTypes.string,
    authorisedBy: PropTypes.string,
    createdBy: PropTypes.string,
    status: PropTypes.string,
    settlement_date: PropTypes.string,
    submission_date: PropTypes.string,
    amount: PropTypes.string.isRequired,
    bacsId: PropTypes.string,
    fromAccountAccountNumber: PropTypes.string,
    fromAccountSortCode: PropTypes.string,
    fromAccountName: PropTypes.string,
    toAccountAccountNumber: PropTypes.string,
    toAccountSortCode: PropTypes.string,
    toAccountName: PropTypes.string,
    hmrcReference: PropTypes.string,
    reference: PropTypes.string,
    transactionCode: PropTypes.string,
  }).isRequired,
};

export default ViewChildPaymentHistory;
